import { Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem, useDisclosure } from "@nextui-org/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { PlusIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { yupResolver } from "@hookform/resolvers/yup";
import useSWR from "swr";
import { materialProductionType } from "../../../../../enums/materialProductionType";
import { getFetcher, postFetcher } from "../../../../../services/api";
import useListCompany from "../../../../../hooks/useListCompany";
import ControlledReactSelect from "../../../../elements/ControlledReactSelect";
import ControlledInput from "../../../../elements/ControlledInput";
import { generateReportProductionValidation } from "../../../../../services/validation/report-production.validation";
import StatusPermohonanRegistrasi from "../../../../../enums/statusRegistrasi";

export default function ModalAddProductionMaterial({ mutate, setSelectedCompany, setSelectedMaterial, setSelectedPeriod }) {
  const {
    register,
    control,
    setValue,
    watch,
    handleSubmit,
    reset,
    formState: { isSubmitting, errors },
  } = useForm({
    resolver: yupResolver(generateReportProductionValidation),
    defaultValues: {
      tipeProdukDihasilkan: "Bahan Berbahaya dan Beracun",
    },
  });
  const tipeProdukDihasilkan = watch("tipeProdukDihasilkan");
  const companyId = watch("companyId");
  const registrasiId = watch("registrasiId");
  const periodId = watch("periodId");
  const dataBahanB3Id = watch("dataBahanB3Id");

  const { isOpen, onOpen, onOpenChange, onClose } = useDisclosure();
  const { data: dataMaterial, isLoading: isLoadingMaterial } = useSWR(`/api/data-master/bahan-b3?returnAll=true`, getFetcher);
  const { data: dataPeriods, isLoading: isLoadingPeriods } = useSWR(`/api/period/actives`, getFetcher);
  const { dataCompany, isLoadingCompany } = useListCompany();
  const [filteredMaterial, setFilteredMaterial] = useState([]);
  const { data: dataRegistration, isLoading: isLoadingRegistration } = useSWR(
    companyId ? `/api/registrasi/find-all/search?returnAll=true&status=${StatusPermohonanRegistrasi.SELESAI}&companyId=${companyId}&type_registrasi=produsen` : null,
    getFetcher
  );

  // Set default period when periods are loaded
  useEffect(() => {
    if (dataPeriods?.data && dataPeriods.data.length > 0 && !periodId) {
      setValue("periodId", dataPeriods.data[0].id);
    }
  }, [dataPeriods, periodId, setValue]);

  useEffect(() => {
    if (dataMaterial && dataRegistration && registrasiId) {
      const filteredRegistrastion = dataRegistration?.registrasi?.find((item) => item.id === registrasiId);
      // Filter dataMaterial.data berdasarkan cas_number yang ada di BahanB3Registrasi
      const filtered = filteredRegistrastion?.BahanB3Registrasi ? dataMaterial.data.filter((material) => filteredRegistrastion.BahanB3Registrasi.some((bahan) => bahan?.cas_number === material?.casNumber)) : dataMaterial.data;
      if (!filtered) {
        toast.error("Tidak ada data bahan B3 dalam SK. Segera update di menu Daftar Registrasi");
      }
      setFilteredMaterial(filtered);
    }
  }, [dataMaterial, dataRegistration, registrasiId]);

  function handleResetValue() {
    setValue("dataBahanB3Id", undefined);
    setValue("registrasiId", undefined);
    setValue("namaProduk", undefined);
  }

  function handleOnClose() {
    reset({
      companyId: "",
      tipeProdukDihasilkan: "Bahan Berbahaya dan Beracun",
      dataBahanB3Id: "",
      registrasiId: "",
      namaProduk: "",
      periodId: "",
    });
    onClose();
  }

  async function handleOnSubmit(data) {
    try {
      console.log(data);
      await postFetcher("/api/pelaporan-b3-dihasilkan/generate", data);
      mutate();
      const company = dataCompany?.data?.find((item) => item.id === data.companyId);
      const period = dataPeriods?.data?.find((item) => item.id === data.periodId);

      setSelectedCompany({ value: company.id, label: company.name });
      setSelectedPeriod({ value: period.id, label: period.name });

      // Set selected material (always B3 type)
      setSelectedMaterial(data.dataBahanB3Id);

      toast.success("Laporan bahan b3 berhasil ditambah!");
      handleOnClose();
    } catch (error) {
      //get response message
      const msg = error?.response?.data?.message ?? "Terjadi kesalahan";
      console.error(error);
      if (msg) toast.error(msg);
      else toast.error("Gagal tambah laporan bahan b3!");
    }
  }

  return (
    <>
      <Button size="sm" onPress={onOpen} color="primary" startContent={<PlusIcon className="size-4" />}>
        Tambah Bahan B3
      </Button>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} onClose={handleOnClose} isDismissable={false} isKeyboardDismissDisabled={false}>
        <ModalContent>
          {(onClose) => (
            <form onSubmit={handleSubmit(handleOnSubmit)}>
              <ModalHeader className="flex flex-col gap-1">Tambah Laporan Produksi Bahan B3</ModalHeader>
              <ModalBody className="flex flex-col gap-3">
                <Select
                  {...register("periodId")}
                  variant="faded"
                  isRequired
                  disallowEmptySelection
                  label="Periode"
                  labelPlacement="outside"
                  placeholder="Pilih Periode..."
                  color={errors.periodId ? "danger" : "default"}
                  isInvalid={errors.periodId}
                  errorMessage={errors.periodId && errors.periodId.message}
                  isLoading={isLoadingPeriods}
                >
                  {dataPeriods?.data?.map((period) => (
                    <SelectItem key={period.id} value={period.id}>
                      {period.name}
                    </SelectItem>
                  ))}
                </Select>

                <Select
                  {...register("tipeProdukDihasilkan")}
                  variant="faded"
                  disallowEmptySelection
                  label="Tipe Produk"
                  labelPlacement="outside"
                  placeholder="Pilih..."
                  defaultSelectedKeys={["Bahan Berbahaya dan Beracun"]}
                  onChange={(e) => {
                    setValue("tipeProdukDihasilkan", e.target.value);
                    handleResetValue();
                  }}
                  color={errors.tipeProdukDihasilkan ? "danger" : "default"}
                  isInvalid={errors.tipeProdukDihasilkan}
                  errorMessage={errors.tipeProdukDihasilkan && errors.tipeProdukDihasilkan.message}
                >
                  {materialProductionType
                    .filter((item) => item === "Bahan Berbahaya dan Beracun")
                    .map((item) => (
                      <SelectItem key={item}>{item}</SelectItem>
                    ))}
                </Select>

                <ControlledReactSelect control={control} name="companyId" label="Perusahaan" options={dataCompany?.data?.map((item) => ({ value: item.id, label: item.name }))} isLoading={isLoadingCompany} />

                <ControlledReactSelect
                  control={control}
                  name="registrasiId"
                  label="SK Registrasi Bahan B3"
                  options={dataRegistration?.registrasi?.map((item) => ({ value: item.id, label: item.nomor_surat_formatted ?? item.nomor_surat }))}
                  isLoading={isLoadingRegistration}
                />
                <ControlledReactSelect
                  control={control}
                  name="dataBahanB3Id"
                  label="Bahan B3"
                  options={filteredMaterial?.map((item) => ({ value: item.id, label: `${item.casNumber} - ${item.namaBahanKimia}` }))}
                  isLoading={isLoadingMaterial}
                />

                {dataBahanB3Id &&
                  registrasiId &&
                  (() => {
                    // Ambil data registrasi yang dipilih
                    const selectedRegistration = dataRegistration?.registrasi?.find((item) => item.id === registrasiId);
                    // Ambil data bahan B3 yang dipilih
                    const selectedBahan = filteredMaterial?.find((item) => item.id === dataBahanB3Id);
                    // Cari no_reg_bahan dari BahanB3Registrasi berdasarkan cas_number yang cocok
                    const bahanRegistrasi = selectedRegistration?.BahanB3Registrasi?.find((bahan) => bahan.cas_number === selectedBahan?.casNumber);
                    const noRegBahan = bahanRegistrasi?.no_reg_bahan || "-";

                    return (
                      <Select variant="faded" label="Nomor Registrasi Bahan" labelPlacement="outside" placeholder="-" isDisabled selectedKeys={[noRegBahan]}>
                        <SelectItem key={noRegBahan}>{noRegBahan}</SelectItem>
                      </Select>
                    );
                  })()}
              </ModalBody>
              <ModalFooter>
                <Button isLoading={isSubmitting} isDisabled={isSubmitting} color="danger" variant="light" onPress={onClose}>
                  Tutup
                </Button>
                <Button isLoading={isSubmitting} isDisabled={isSubmitting} type="submit" color="primary">
                  Simpan
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </>
  );
}
